/**
 * CMS SDK - 高级组件: 富文本编辑器、媒体库、版本历史
 */

import React, { useState, useCallback, useRef } from 'react';
import { t } from '../i18n';

// 富文本工具栏
export function EditorToolbar({ onAction, activeFormats = [] }) {
  const tools = [
    { action: 'bold', icon: 'B', label: 'Bold' },
    { action: 'italic', icon: 'I', label: 'Italic' },
    { action: 'underline', icon: 'U', label: 'Underline' },
    { action: 'strike', icon: 'S', label: 'Strikethrough' },
    { divider: true },
    { action: 'h1', icon: 'H1', label: 'Heading 1' },
    { action: 'h2', icon: 'H2', label: 'Heading 2' },
    { action: 'h3', icon: 'H3', label: 'Heading 3' },
    { divider: true },
    { action: 'ul', icon: '•', label: 'Bullet List' },
    { action: 'ol', icon: '1.', label: 'Numbered List' },
    { action: 'quote', icon: '"', label: 'Quote' },
    { divider: true },
    { action: 'link', icon: '🔗', label: 'Link' },
    { action: 'image', icon: '🖼️', label: 'Image' },
    { action: 'code', icon: '</>', label: 'Code' },
  ];

  return (
    <div className="cms-editor-toolbar">
      {tools.map((tool, index) =>
        tool.divider ? (
          <div key={index} className="cms-toolbar-divider" />
        ) : (
          <button
            key={tool.action}
            className={`cms-toolbar-btn ${activeFormats.includes(tool.action) ? 'active' : ''}`}
            onClick={() => onAction?.(tool.action)}
            title={tool.label}
          >
            {tool.icon}
          </button>
        )
      )}
    </div>
  );
}

// 简单富文本编辑器
export function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'Start writing...',
  minHeight = 300,
  className = '',
}) {
  const editorRef = useRef(null);
  const [activeFormats, setActiveFormats] = useState([]);

  const handleAction = useCallback((action) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    document.execCommand(action === 'h1' ? 'formatBlock' : action, false, 
      action.startsWith('h') ? `<${action}>` : null);
    
    onChange?.(editorRef.current.innerHTML);
  }, [onChange]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange?.(editorRef.current.innerHTML);
    }
  }, [onChange]);

  return (
    <div className={`cms-rich-editor ${className}`}>
      <EditorToolbar onAction={handleAction} activeFormats={activeFormats} />
      <div
        ref={editorRef}
        className="cms-editor-content"
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        style={{ minHeight }}
        dangerouslySetInnerHTML={{ __html: value }}
        data-placeholder={placeholder}
      />
    </div>
  );
}

// 媒体库网格
export function MediaGrid({
  items = [],
  loading = false,
  onSelect,
  onDelete,
  selectedIds = [],
  selectable = true,
  className = '',
}) {
  if (loading) {
    return (
      <div className={`cms-media-grid ${className}`}>
        <div className="cms-loading"><div className="cms-spinner" /></div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={`cms-media-grid ${className}`}>
        <div className="cms-empty">
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🖼️</div>
          <div>{t('noMedia')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`cms-media-grid ${className}`}>
      {items.map((item) => {
        const isSelected = selectedIds.includes(item.id);
        const isImage = item.type?.startsWith('image') || /\.(jpg|jpeg|png|gif|webp)$/i.test(item.url);

        return (
          <div
            key={item.id}
            className={`cms-media-item ${isSelected ? 'selected' : ''}`}
            onClick={() => selectable && onSelect?.(item)}
          >
            <div className="cms-media-preview">
              {isImage ? (
                <img src={item.url || item.thumbnail} alt={item.name} />
              ) : (
                <div className="cms-media-icon">📄</div>
              )}
            </div>
            <div className="cms-media-info">
              <div className="cms-media-name">{item.name}</div>
              <div className="cms-media-size">{formatFileSize(item.size)}</div>
            </div>
            {selectable && (
              <div className={`cms-media-checkbox ${isSelected ? 'checked' : ''}`}>
                {isSelected && '✓'}
              </div>
            )}
            {onDelete && (
              <button
                className="cms-media-delete"
                onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
              >
                ×
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// 版本历史列表
export function VersionHistory({
  versions = [],
  currentVersion,
  onRestore,
  onPreview,
  loading = false,
  className = '',
}) {
  if (loading) {
    return <div className={`cms-version-list ${className}`}><div className="cms-loading"><div className="cms-spinner" /></div></div>;
  }

  if (versions.length === 0) {
    return (
      <div className={`cms-version-list ${className}`}>
        <div className="cms-empty" style={{ padding: '32px' }}>{t('noVersions')}</div>
      </div>
    );
  }

  return (
    <div className={`cms-version-list ${className}`}>
      {versions.map((version, index) => {
        const isCurrent = version.id === currentVersion || index === 0;
        
        return (
          <div key={version.id} className={`cms-version-item ${isCurrent ? 'current' : ''}`}>
            <div className="cms-version-info">
              <div className="cms-version-number">
                v{version.version || versions.length - index}
                {isCurrent && <span className="cms-version-badge">{t('current')}</span>}
              </div>
              <div className="cms-version-meta">
                {version.author?.name || 'Unknown'} · {new Date(version.created_at).toLocaleString()}
              </div>
              {version.message && (
                <div className="cms-version-message">{version.message}</div>
              )}
            </div>
            <div className="cms-version-actions">
              {onPreview && (
                <button className="cms-btn cms-btn-sm" onClick={() => onPreview(version)}>
                  {t('preview')}
                </button>
              )}
              {onRestore && !isCurrent && (
                <button className="cms-btn cms-btn-sm cms-btn-outline" onClick={() => onRestore(version)}>
                  {t('restore')}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// 内容状态工作流
export function ContentWorkflow({ status, onStatusChange, allowedTransitions = [] }) {
  const statusConfig = {
    draft: { label: t('draft'), color: '#6b7280', icon: '📝' },
    review: { label: t('review'), color: '#f59e0b', icon: '👁️' },
    approved: { label: t('approved'), color: '#22c55e', icon: '✓' },
    published: { label: t('published'), color: '#3b82f6', icon: '🌐' },
    archived: { label: t('archived'), color: '#9ca3af', icon: '📦' },
  };

  const config = statusConfig[status] || statusConfig.draft;

  return (
    <div className="cms-workflow">
      <div className="cms-workflow-current" style={{ borderColor: config.color }}>
        <span>{config.icon}</span>
        <span>{config.label}</span>
      </div>
      {allowedTransitions.length > 0 && (
        <div className="cms-workflow-actions">
          {allowedTransitions.map(nextStatus => {
            const nextConfig = statusConfig[nextStatus];
            return (
              <button
                key={nextStatus}
                className="cms-btn cms-btn-sm"
                style={{ background: nextConfig?.color }}
                onClick={() => onStatusChange?.(nextStatus)}
              >
                {nextConfig?.icon} {nextConfig?.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default RichTextEditor;
